import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Download } from "lucide-react";
import QRCode from "qrcode";
import { formatDateLong } from "@/utils/dateFormat";
import { formatFileSize } from "@/utils/fileUtils";

const MANIFEST_PATH = "apps/trackcast/latest.json";
const APK_PATH = "apps/trackcast/latest.apk";

interface TrackcastReleaseManifest {
  version: string;
  sizeBytes: number;
  sha256: string;
  uploadedAt: string;
}

function isManifest(value: unknown): value is TrackcastReleaseManifest {
  if (typeof value !== "object" || value === null) return false;
  const m = value as Record<string, unknown>;
  return (
    typeof m.version === "string" &&
    m.version.length > 0 &&
    typeof m.sizeBytes === "number" &&
    Number.isFinite(m.sizeBytes) &&
    m.sizeBytes > 0 &&
    typeof m.sha256 === "string" &&
    typeof m.uploadedAt === "string" &&
    !Number.isNaN(Date.parse(m.uploadedAt))
  );
}

export default function TrackcastAndroidPage() {
  const { t } = useTranslation("pages");
  const [manifest, setManifest] = useState<TrackcastReleaseManifest | null>(
    null,
  );
  const [status, setStatus] = useState<"loading" | "ready" | "empty">(
    "loading",
  );
  const [qrSvg, setQrSvg] = useState<string | null>(null);

  const baseUrl = (import.meta.env.VITE_TRACKCAST_DIST_URL || "").replace(
    /\/+$/,
    "",
  );
  const apkUrl = baseUrl ? `${baseUrl}/${APK_PATH}` : null;

  useEffect(() => {
    if (!baseUrl) {
      setStatus("empty");
      return;
    }
    let cancelled = false;
    fetch(`${baseUrl}/${MANIFEST_PATH}`, { cache: "no-store" })
      .then((res) => {
        if (!res.ok) throw new Error(`manifest fetch failed: ${res.status}`);
        return res.json();
      })
      .then((body: unknown) => {
        if (cancelled) return;
        if (!isManifest(body)) throw new Error("invalid manifest");
        setManifest(body);
        setStatus("ready");
      })
      .catch(() => {
        if (!cancelled) setStatus("empty");
      });
    return () => {
      cancelled = true;
    };
  }, [baseUrl]);

  useEffect(() => {
    if (!apkUrl) return;
    let cancelled = false;
    QRCode.toString(apkUrl, { type: "svg", margin: 1 })
      .then((svg) => {
        if (!cancelled) setQrSvg(svg);
      })
      .catch(() => {
        if (!cancelled) setQrSvg(null);
      });
    return () => {
      cancelled = true;
    };
  }, [apkUrl]);

  return (
    <div className="page-container pb-16">
      <div className="mb-6">
        <p className="text-[11px] font-semibold tracking-[0.2em] uppercase text-secondary-500 mb-1.5">
          {t("trackcast.android.eyebrow")}
        </p>
        <h1 className="page-title">{t("trackcast.android.title")}</h1>
        <p className="page-subtitle">{t("trackcast.android.subtitle")}</p>
      </div>

      {status === "loading" && (
        <p className="text-sm text-secondary-500">
          {t("trackcast.android.loading")}
        </p>
      )}

      {status === "empty" && (
        <p className="text-sm text-secondary-500">
          {t("trackcast.android.empty")}
        </p>
      )}

      {status === "ready" && manifest && apkUrl && (
        <div className="bg-white border border-secondary-200 rounded-xl p-6 grid gap-8 md:grid-cols-[auto_1fr] md:items-center">
          <div className="flex flex-col items-center gap-3">
            {qrSvg ? (
              <div
                role="img"
                aria-label={t("trackcast.android.qrAlt")}
                className="w-52 h-52 [&>svg]:w-full [&>svg]:h-full"
                dangerouslySetInnerHTML={{ __html: qrSvg }}
              />
            ) : (
              <div
                role="img"
                aria-label={t("trackcast.android.qrAlt")}
                className="w-52 h-52 border border-dashed border-secondary-200 rounded-lg"
              />
            )}
            <a href={apkUrl} className="btn-primary px-5">
              <Download className="h-4 w-4" />
              {t("trackcast.android.download")}
            </a>
          </div>
          <div>
            <dl className="grid grid-cols-[104px_1fr] gap-x-3.5 gap-y-2 text-sm">
              <dt className="pt-0.5 font-mono text-[10.5px] tracking-[0.12em] uppercase text-secondary-400">
                {t("trackcast.android.version")}
              </dt>
              <dd className="font-semibold text-secondary-900">
                {manifest.version}
              </dd>
              <dt className="pt-0.5 font-mono text-[10.5px] tracking-[0.12em] uppercase text-secondary-400">
                {t("trackcast.android.uploadedAt")}
              </dt>
              <dd className="text-secondary-700">
                {formatDateLong(manifest.uploadedAt)}
              </dd>
              <dt className="pt-0.5 font-mono text-[10.5px] tracking-[0.12em] uppercase text-secondary-400">
                {t("trackcast.android.size")}
              </dt>
              <dd className="text-secondary-700">
                {formatFileSize(manifest.sizeBytes)}
              </dd>
            </dl>
            <p className="mt-5 text-xs text-secondary-400">
              {t("trackcast.android.installHint")}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
